import {LineItemModel} from '../../../../domain/models/checkout/LineItems/LineItem.model';
import {
  SubscriptionFrequency,
  SubscriptionSettingsModel,
} from '../../../../domain/models/checkout/LineItems/SubscriptionSettings.model';
import {ViolationModel, ViolationSeverity} from '../../../../domain/models/checkout/CartSummary/Violation.model';
import {ILocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {PaymentOptionType} from '../../../../domain/models/checkout/LineItems/PaymentConfig.model';

export function getSubscriptionDuration(
  subscriptionSettings: SubscriptionSettingsModel,
  localeKeys: ILocaleKeys
): string {
  if (subscriptionSettings.autoRenewal) {
    return localeKeys.checkout.pricingPlans.duration.label({
      number: undefined,
      frequency: localeKeys.checkout.page_plan_autorenew(),
    });
  }

  const cycles = subscriptionSettings.billingCycles * subscriptionSettings.interval;
  const frequency = getFrequencyText({
    frequency: subscriptionSettings.frequency,
    cycles,
    localeKeys,
  });

  return localeKeys.checkout.pricingPlans.duration.label({
    number: subscriptionSettings.interval > 0 ? cycles : undefined,
    frequency,
  });
}

export function getFrequencyText({
  frequency,
  cycles,
  localeKeys,
}: {
  frequency: SubscriptionFrequency;
  cycles: number;
  localeKeys: ILocaleKeys;
}) {
  switch (frequency) {
    case SubscriptionFrequency.DAY: {
      return cycles > 1
        ? localeKeys.checkout.pricingPlans.frequency.days.plural()
        : localeKeys.checkout.pricingPlans.frequency.day.singular();
    }
    case SubscriptionFrequency.WEEK: {
      return cycles > 1
        ? localeKeys.checkout.pricingPlans.frequency.weeks.plural()
        : localeKeys.checkout.pricingPlans.frequency.week.singular();
    }
    case SubscriptionFrequency.MONTH: {
      return cycles > 1
        ? localeKeys.checkout.pricingPlans.frequency.months.plural()
        : localeKeys.checkout.pricingPlans.frequency.month.singular();
    }
    case SubscriptionFrequency.YEAR: {
      return cycles > 1
        ? localeKeys.checkout.pricingPlans.frequency.years.plural()
        : localeKeys.checkout.pricingPlans.frequency.year.singular();
    }
    case SubscriptionFrequency.UNDEFINED: {
      return localeKeys.checkout.page_plan_untilCanceled();
    }
  }
}
function getDepositLabel(lineItem: LineItemModel, localeKeys: ILocaleKeys) {
  return lineItem.paymentConfig.depositAmount?.amount > 0
    ? localeKeys.checkout.myCartPaymentMethod.deposit.lineItem({
        price: lineItem.paymentConfig.depositAmount?.formattedAmount,
      })
    : '';
}

export function getPaymentTypeLabel(lineItem: LineItemModel, localeKeys: ILocaleKeys): string {
  const membershipName = lineItem.paymentConfig.selectedMembership?.name;

  switch (lineItem.paymentConfig.paymentOption) {
    case PaymentOptionType.DEPOSIT_ONLINE:
      return getDepositLabel(lineItem, localeKeys);
    case PaymentOptionType.MEMBERSHIP:
    case PaymentOptionType.MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER:
      return membershipName
        ? localeKeys.checkout.myCartPaymentMethod.onlinePlan.lineItem({membership: membershipName})
        : localeKeys.checkout.myCartPaymentMethod.payWithPlan.lineItem();
    case PaymentOptionType.MEMBERSHIP_OFFLINE:
      return membershipName
        ? localeKeys.checkout.myCartPaymentMethod.offlinePlan.lineItemWithId({membership: membershipName})
        : localeKeys.checkout.myCartPaymentMethod.offlinePlan.lineItemWithoutId();
    case PaymentOptionType.FULL_PAYMENT_OFFLINE:
      return lineItem.pricing.totalPrice.amount > 0
        ? localeKeys.checkout.myCartPaymentMethod.offlinePayment.lineItem()
        : '';
    default:
      return '';
  }
}

export function isLineItemErrorViolationExist(violations: ViolationModel[]): boolean {
  return violations.some(
    (violation) => !!violation.target?.lineItemTarget && violation.severity === ViolationSeverity.error
  );
}
export function getLineItemViolationIfItExists(
  violations: ViolationModel[],
  lineItemId: string
): ViolationModel | undefined {
  return (
    violations.find(
      (violation) =>
        violation.target?.lineItemTarget?.id === lineItemId && violation.severity === ViolationSeverity.error
    ) ??
    violations.find(
      (violation) =>
        violation.target?.lineItemTarget?.id === lineItemId && violation.severity === ViolationSeverity.warning
    )
  );
}
